import React, { useState } from "react";
// Removed unused import: import { Navigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";
import { Eye, EyeOff } from "lucide-react";

function Register() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const { theme } = useTheme();

  async function handleRegister(ev) {
    ev.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMsg("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: "POST",
        body: JSON.stringify({ username, email, password }),
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMsg(data.message || "Registration request submitted. Await admin approval.");
        setUsername(""); setPassword(""); setConfirmPassword(""); setEmail("");
      } else {
        setError(data.error || "Registration failed. Please try a different username or email.");
      }
    } catch (err) {
      setError("Registration failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  // Removed unused state: if (redirect) { return <Navigate to="/login" />; }

  const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-blue-600';
  const inputBorder = theme === 'dark' ? 'border-gray-600' : 'border-gray-300';
  const inputFocusRing = theme === 'dark' ? 'focus:ring-yellow-400' : 'focus:ring-blue-400';
  const buttonGradientFrom = theme === 'dark' ? 'from-yellow-500' : 'from-blue-500';
  const buttonGradientTo = theme === 'dark' ? 'to-orange-600' : 'to-indigo-600';
  const buttonHoverFrom = theme === 'dark' ? 'hover:from-yellow-600' : 'hover:from-blue-600';
  const buttonHoverTo = theme === 'dark' ? 'hover:to-orange-700' : 'hover:to-indigo-700';

  return (
    <div className={`max-w-md mx-auto ${bgColor} rounded-xl shadow-lg p-8 mt-10 mb-10`}>
      <h2 className={`text-3xl font-bold mb-6 text-center ${textColor}`}>Register</h2>
      {error && <div className="mb-4 text-red-600 text-center font-semibold">{error}</div>}
      {successMsg && <div className="mb-4 text-green-600 text-center font-semibold">{successMsg}</div>}
      <form onSubmit={handleRegister} className="space-y-6">
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(ev) => setUsername(ev.target.value)}
          className={`w-full px-4 py-2 border ${inputBorder} rounded-lg focus:outline-none ${inputFocusRing} text-lg ${theme === 'dark' ? 'bg-gray-700 text-white' : 'text-gray-800'}`}
          disabled={loading}
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          className={`w-full px-4 py-2 border ${inputBorder} rounded-lg focus:outline-none ${inputFocusRing} text-lg ${theme === 'dark' ? 'bg-gray-700 text-white' : 'text-gray-800'}`}
          disabled={loading}
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            className={`w-full px-4 py-2 border ${inputBorder} rounded-lg focus:outline-none ${inputFocusRing} text-lg pr-12 ${theme === 'dark' ? 'bg-gray-700 text-white' : 'text-gray-800'}`}
            disabled={loading}
          />
          <button
            type="button"
            tabIndex={-1}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400 hover:text-yellow-500' : 'text-gray-500 hover:text-blue-600'} focus:outline-none`}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(ev) => setConfirmPassword(ev.target.value)}
            className={`w-full px-4 py-2 border ${inputBorder} rounded-lg focus:outline-none ${inputFocusRing} text-lg pr-12 ${theme === 'dark' ? 'bg-gray-700 text-white' : 'text-gray-800'}`}
            disabled={loading}
          />
          <button
            type="button"
            tabIndex={-1}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400 hover:text-yellow-500' : 'text-gray-500 hover:text-blue-600'} focus:outline-none`}
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <button
          type="submit"
          className={`w-full flex justify-center items-center bg-gradient-to-r ${buttonGradientFrom} ${buttonGradientTo} text-white font-semibold py-3 rounded-lg shadow ${buttonHoverFrom} ${buttonHoverTo} transition text-lg disabled:opacity-75`}
          disabled={loading}
        >
          {loading ? (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : 'Register'}
        </button>
      </form>
      <div className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} text-sm text-center`}>
        After submitting, your account will be reviewed by an admin. You will receive an email when your account is approved.
      </div>
    </div>
  );
}

export default Register;